import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClockCounterClockwise, ArrowUUpLeft } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Last 5 ELO repair runs at a glance, with one-click rollback.
 *
 * Hits:
 *   GET  /api/admin/repair-history?limit=5
 *   POST /api/admin/repair-rollback   { batch_id }
 */
export default function RepairHistoryCard() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/repair-history?limit=5`, { withCredentials: true });
      setBatches(r.data?.batches || []);
    } catch (err) {
      // 401/403 just means non-admin viewing — skip silently
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Failed to load repair history");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rollback = async (batch) => {
    if (!window.confirm(
      `Roll back this repair?\n\n` +
      `• Run on: ${formatDate(batch.taken_at)}\n` +
      `• By: ${batch.taken_by || "unknown"}\n` +
      `• Restores ${batch.player_count} player${batch.player_count === 1 ? "" : "s"} to their pre-repair ELO + tier\n` +
      `• Best-effort cleanup of replayed match deltas\n\n` +
      `This is itself reversible — running the repair again with the same baselines will reapply.`
    )) return;
    const pw = await promptSuperPassword({
      title: "Roll back this repair?",
      description: "Restores players to their pre-repair ELO + tier.",
      actionLabel: "Rollback Repair",
    });
    if (!pw) return;
    setRollingBack(batch.batch_id);
    try {
      const r = await axios.post(
        `${API}/admin/repair-rollback`,
        { batch_id: batch.batch_id },
        { headers: { "X-Super-Password": pw }, withCredentials: true },
      );
      toast.success(r.data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  return (
    <Card className="bg-card border-chart-2/30" data-testid="repair-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClockCounterClockwise className="w-5 h-5 text-chart-2" weight="fill" />
          Last Repair History
        </CardTitle>
        <CardDescription className="text-xs leading-relaxed">
          Last 5 ELO repair runs (Order Editor + Repair card). Click a row's
          rollback button to restore that snapshot's pre-repair ELO + tier.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && batches.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">Loading…</p>
        ) : batches.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">
            No repair runs recorded yet. Use the ELO Order Editor or Repair card to make changes.
          </p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div
                key={b.batch_id}
                className="border border-border rounded-lg p-3 bg-background/40"
                data-testid={`repair-history-row-${b.batch_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold tabular-nums">
                        {formatDate(b.taken_at)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">by {b.taken_by || "unknown"}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-chart-2/15 text-chart-2 tabular-nums">
                        {b.player_count} player{b.player_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    {b.summary?.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {b.summary.map((s, i) => (
                          <li key={i} className="text-[11px] text-muted-foreground tabular-nums truncate">
                            <span className="text-foreground/80">{s.name}</span>:{" "}
                            {s.elo_before} → {s.elo_after}
                            {s.tier_before !== s.tier_after && (
                              <span className="text-secondary ml-1">
                                · {s.tier_before} → {s.tier_after}
                              </span>
                            )}
                          </li>
                        ))}
                        {b.player_count > 3 && (
                          <li className="text-[10px] text-muted-foreground italic">
                            …and {b.player_count - 3} more
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rollback(b)}
                    disabled={rollingBack === b.batch_id}
                    className="border-chart-2/40 text-chart-2 hover:bg-chart-2/10 text-xs h-7 shrink-0"
                    data-testid={`repair-history-rollback-${b.batch_id}`}
                  >
                    <ArrowUUpLeft className="w-3 h-3 mr-1" weight="bold" />
                    {rollingBack === b.batch_id ? "Rolling back…" : "Rollback"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
